// packages/web-ui/src/AppShell/AppShell.tsx
//
// W2-S2 — the four-region AppShell. Composes:
//   <HealthStatusBanner>
//   ┌─────────────────────────────────────────────┐
//   │ <ActivityBar>  <ContextPanel>  <TabContentArea>  <UtilityDrawer> │
//   └─────────────────────────────────────────────┘
//                                  └─ <StatusBar>
//
// Source of truth: AC #1 / #2 / #3 / #6 / #7 / #8 of story 2-2.

import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { JSX } from 'react';
import { ActivityBar } from './ActivityBar.js';
import { ContextPanel } from './ContextPanel.js';
import type { ContextPanelMode } from './ContextPanel.js';
import { DragHandle } from './DragHandle.js';
import { HealthStatusBanner } from './HealthStatusBanner.js';
import { StatusBar } from './StatusBar.js';
import { TabContentArea } from './TabContentArea.js';
import { UtilityDrawer } from './UtilityDrawer.js';
import { layoutStorage } from './layout-storage.js';
import { resolveLayoutModes } from './layout-modes.js';
import {
  ARIA_LIVE_ANNOUNCE_DELAY_MS,
  DESTINATION_LABELS,
  Z_INDEX,
} from './responsive-tokens.js';
import { useBreakpoint } from './useBreakpoint.js';
import { useDestinationKeybindings } from './useDestinationKeybindings.js';
import { useTabKeybindings } from './useTabKeybindings.js';
import { useHashRoute } from '../router/hash-router.js';
import type { HashRoute } from '../router/hash-router.js';
import type { ActiveDestination } from '../state/navigation.js';
import { useRootStore } from '../state/store.js';
import { SkipLink } from '../components/SkipLink/index.js';
import { KeyboardCheatSheet } from '../components/welcome/KeyboardCheatSheet.js';
import { UserDocsModal, OPEN_DOCS_EVENT } from '../components/welcome/UserDocsModal.js';
import { useGlobalA11y } from '../hooks/useGlobalA11y.js';
import { useKeymap, useGlobalKeymap } from '../hooks/useKeymap.js';

const QUERY_EMBED_FLAG = 'clientMode';
const EMBED_VALUE = 'embedded';

export function AppShell(): JSX.Element {
  const route: HashRoute = useHashRoute();
  const breakpoint = useBreakpoint();

  // W2-S3 — global Ctrl+1..5 / Ctrl+, / F1 keybindings. Single source of
  // truth at AppShell level (Pitfall #18 — DO NOT add deps to the hook).
  useDestinationKeybindings();

  // W2 fix-pass §1.3 — global Ctrl+W / Ctrl+Tab / Ctrl+Shift+Tab tab
  // keybindings. The hook was authored in W2-S4 but never invoked at
  // AppShell mount; the wiring audit (W-§1.3) flagged this as MED. Build
  // the three callbacks against the navigation slice's `closeTab` +
  // `setActiveTab` actions, scanning `tabOrder` to pick the cycle target.
  const closeActiveTab = useCallback((): void => {
    const state = useRootStore.getState();
    if (state.activeTabId === null) return;
    state.closeTab(state.activeTabId);
  }, []);
  const cycleNextTab = useCallback((): void => {
    const state = useRootStore.getState();
    const order = state.tabOrder;
    if (order.length <= 1 || state.activeTabId === null) return;
    const idx = order.indexOf(state.activeTabId);
    if (idx < 0) return;
    const next = order[(idx + 1) % order.length];
    state.setActiveTab(next);
  }, []);
  const cyclePrevTab = useCallback((): void => {
    const state = useRootStore.getState();
    const order = state.tabOrder;
    if (order.length <= 1 || state.activeTabId === null) return;
    const idx = order.indexOf(state.activeTabId);
    if (idx < 0) return;
    const prev = order[(idx - 1 + order.length) % order.length];
    state.setActiveTab(prev);
  }, []);
  useTabKeybindings({ closeActiveTab, cycleNextTab, cyclePrevTab });

  // W2-S10b Task 4 (AC #4 / #5) — mount the canonical a11y primitives at the
  // AppShell root: ensures the singleton aria-live announcer region exists for
  // panel-level `announce()` calls and writes the `--reduced-motion: 1|0` CSS
  // var on `<html>` so descendant CSS rules can opt out of animations.
  useGlobalA11y();

  // W2-S10b Task 3.3 (AC #3) — global cheat-sheet listener mount. The hook
  // attaches its own keydown listener; we render the modal based on `isOpen`.
  const cheatSheet = useKeymap();

  // User-docs modal — opens via the `tessa:openDocs` window event dispatched
  // from `<HelpPopover>`'s "Open docs" menu item. Esc / backdrop / close
  // button all route through `setDocsOpen(false)`.
  const [docsOpen, setDocsOpen] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    function onOpen(): void {
      setDocsOpen(true);
    }
    window.addEventListener(OPEN_DOCS_EVENT, onOpen);
    return (): void => {
      window.removeEventListener(OPEN_DOCS_EVENT, onOpen);
    };
  }, []);

  // Polish §8 — wiring-audit additions: Ctrl+E (export view), Ctrl+Shift+J
  // (debug-tab toggle channel), 1/2/3 (cycle drawer tab), F6 (cycle panel
  // focus), Ctrl+N (new session). Each binding fires a `tessa:*` window
  // CustomEvent; action-target components register their own listeners.
  useGlobalKeymap();

  const activeDestination = useRootStore((s) => s.activeDestination);
  const activeResourceId = useRootStore((s) => s.activeResourceId);
  const clientMode = useRootStore((s) => s.clientMode);
  const contextPanelWidth = useRootStore((s) => s.contextPanelWidth);
  const utilityDrawerWidth = useRootStore((s) => s.utilityDrawerWidth);
  const contextPanelCollapsed = useRootStore((s) => s.contextPanelCollapsed);
  const utilityDrawerOpen = useRootStore((s) => s.utilityDrawerOpen);

  const setRoute = useRootStore((s) => s.setRoute);
  const setContextPanelWidth = useRootStore((s) => s.setContextPanelWidth);
  const setUtilityDrawerWidth = useRootStore((s) => s.setUtilityDrawerWidth);
  const setClientMode = useRootStore((s) => s.setClientMode);
  const resetContextPanelWidth = useRootStore((s) => s.resetContextPanelWidth);
  const resetUtilityDrawerWidth = useRootStore((s) => s.resetUtilityDrawerWidth);
  const restoreTabs = useRootStore((s) => s.restoreTabs);

  // ─── W2-S4 AC #3: hydrate persisted tabs once before reconciling routes ──
  // Idempotent — slice tracks `_tabsRestored` flag, so the call from store
  // module-load + this mount-time call coalesce.
  const tabsHydrated = useRef(false);
  if (!tabsHydrated.current) {
    tabsHydrated.current = true;
    restoreTabs();
  }

  // ─── Sync hash route → navigation slice ───────────────────────────────────
  useEffect(() => {
    setRoute(route);
  }, [route, setRoute]);

  // ─── Embed-mode detection (AC #6) ─────────────────────────────────────────
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get(QUERY_EMBED_FLAG) === EMBED_VALUE) {
      setClientMode('embedded');
    }
  }, [setClientMode]);

  // ─── Restore persisted widths from localStorage (AC #2) ──────────────────
  const widthsHydrated = useRef(false);
  useEffect(() => {
    if (widthsHydrated.current) return;
    widthsHydrated.current = true;
    const ctx = layoutStorage.readContextPanelWidth();
    if (ctx !== null) setContextPanelWidth(ctx);
    const drawer = layoutStorage.readUtilityDrawerWidth();
    if (drawer !== null) setUtilityDrawerWidth(drawer);
  }, [setContextPanelWidth, setUtilityDrawerWidth]);

  // ─── Resolve responsive panel modes (AC #3) ──────────────────────────────
  const layout = useMemo(
    () => resolveLayoutModes(breakpoint, clientMode),
    [breakpoint, clientMode],
  );

  // ─── Settings + Skills collapse ContextPanel to 0 px (AC #1 + UX §5 mode F).
  // Both destinations host their own internal split so the global rail
  // would be redundant whitespace next to the in-pane list.
  const settingsForcesCollapse =
    activeDestination === 'settings' || activeDestination === 'skills';
  const effectiveContextCollapsed = contextPanelCollapsed || settingsForcesCollapse;

  // Resize handle is disabled when the panel is in overlay/popover/hidden mode.
  const contextResizeDisabled =
    layout.contextPanelMode !== 'inline' || effectiveContextCollapsed;
  const drawerResizeDisabled = layout.utilityDrawerMode !== 'inline' || !utilityDrawerOpen;

  // ─── Context-panel width — fall back to per-breakpoint default ───────────
  const effectiveContextWidth = useMemo<ContextPanelWidthResolution>(
    () => ({
      width: contextPanelWidth || layout.contextPanelDefaultWidth,
      mode: layout.contextPanelMode,
    }),
    [contextPanelWidth, layout.contextPanelDefaultWidth, layout.contextPanelMode],
  );

  // ─── Drag handlers + localStorage persistence (AC #2) ────────────────────
  const handleContextResize = useCallback(
    (newWidth: number): void => {
      setContextPanelWidth(newWidth);
      // Persist the *clamped* width — read it back from the store after set.
      layoutStorage.writeContextPanelWidth(useRootStore.getState().contextPanelWidth);
    },
    [setContextPanelWidth],
  );
  const handleContextReset = useCallback((): void => {
    resetContextPanelWidth();
    layoutStorage.writeContextPanelWidth(useRootStore.getState().contextPanelWidth);
  }, [resetContextPanelWidth]);

  const handleDrawerResize = useCallback(
    (newWidth: number): void => {
      setUtilityDrawerWidth(newWidth);
      layoutStorage.writeUtilityDrawerWidth(useRootStore.getState().utilityDrawerWidth);
    },
    [setUtilityDrawerWidth],
  );
  const handleDrawerReset = useCallback((): void => {
    resetUtilityDrawerWidth();
    layoutStorage.writeUtilityDrawerWidth(useRootStore.getState().utilityDrawerWidth);
  }, [resetUtilityDrawerWidth]);

  // ─── aria-live destination announcer (AC #8) ─────────────────────────────
  // UX §4.6 specs the announce on **destination switch**. Re-announcing on
  // every resource-id change (e.g. navigating between KB entities under the
  // same destination) would screen-reader-spam — see Pitfall #11. Depend
  // only on `activeDestination`.
  const [announceText, setAnnounceText] = useState('');
  useEffect(() => {
    const timeoutMs = prefersReducedMotion() ? 0 : ARIA_LIVE_ANNOUNCE_DELAY_MS;
    const handle = setTimeout(() => {
      setAnnounceText(`${labelFor(activeDestination)} panel active`);
    }, timeoutMs);
    return () => {
      clearTimeout(handle);
    };
  }, [activeDestination]);

  // ─── Render the tree ─────────────────────────────────────────────────────
  return (
    <div
      className="bg-cream-50 flex h-screen flex-col overflow-hidden"
      data-testid="app-shell-root"
      data-breakpoint={breakpoint}
      data-client-mode={clientMode}
    >
      <SkipLink />
      <HealthStatusBanner hostedInVsCode={clientMode === 'embedded'} />

      {layout.activityBarHorizontal ? (
        <ActivityBar breakpoint={breakpoint} clientMode={clientMode} />
      ) : null}

      <div className="flex flex-1 overflow-hidden" style={{ zIndex: Z_INDEX.baseCanvas }}>
        {layout.activityBarHorizontal ? null : (
          <ActivityBar breakpoint={breakpoint} clientMode={clientMode} />
        )}

        {layout.contextPanelMode !== 'hidden' ? (
          <Fragment>
            <ContextPanel
              width={effectiveContextWidth.width}
              collapsed={effectiveContextCollapsed}
              mode={effectiveContextWidth.mode}
              breakpoint={breakpoint}
              activeDestination={activeDestination}
            />
            <DragHandle
              side="left"
              currentWidth={effectiveContextWidth.width}
              disabled={contextResizeDisabled}
              onResize={handleContextResize}
              onReset={handleContextReset}
              testId="context-panel-resize-handle"
            />
          </Fragment>
        ) : null}

        <TabContentArea footer={<StatusBar />} />

        {layout.utilityDrawerMode !== 'hidden' ? (
          <Fragment>
            <DragHandle
              side="right"
              currentWidth={utilityDrawerWidth}
              disabled={drawerResizeDisabled}
              onResize={handleDrawerResize}
              onReset={handleDrawerReset}
              testId="utility-drawer-resize-handle"
            />
            <UtilityDrawer
              width={utilityDrawerWidth}
              open={utilityDrawerOpen}
              mode={layout.utilityDrawerMode}
              breakpoint={breakpoint}
            />
          </Fragment>
        ) : null}
      </div>

      <div role="status" aria-live="polite" className="sr-only" data-testid="aria-live-announcer">
        {announceText}
      </div>

      {/* W2-S10b Task 3.3 (AC #3) — master keyboard cheat-sheet overlay,
          opened by the `useKeymap()` hook on `?` / `Ctrl+/`. */}
      <KeyboardCheatSheet isOpen={cheatSheet.isOpen} onClose={cheatSheet.close} />

      {/* User-facing documentation modal — opened by the Help button's
          "Open docs" item (window event `tessa:openDocs`). */}
      <UserDocsModal isOpen={docsOpen} onClose={(): void => { setDocsOpen(false); }} />
    </div>
  );
}

interface ContextPanelWidthResolution {
  width: number;
  mode: ContextPanelMode;
}

function labelFor(destination: ActiveDestination): string {
  return DESTINATION_LABELS[destination] ?? DESTINATION_LABELS.chat;
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}
